import React, { useEffect, useState } from 'react';

import { AreaChartOutlined } from '@ant-design/icons';
import { TLongTermOptionValue } from '@models/home';
import { formatNumberWithDigits, isLeapYear } from '@utils/common';
import { Skeleton } from 'antd';
import dayjs from 'dayjs';
import ReactECharts from 'echarts-for-react';

import './DashboardAreaChart.scss';

// ChartData example:
//   chartData={{
//     time: [
//       '2021-06-01',
//       '2021-06-02',
//       '2021-06-03',
//       '2021-06-04',
//       '2021-06-05',
//       '2021-06-06',
//     ],
//     dataset: [
//       {
//         name: '測試',
//         data: [10, 20, 30, 40, 50, 60],
//         color: '#B877F9',
//       },
//       {
//         name: '測試二',
//         data: [20, 30, 40, 50, 60, 70],
//         color: '#6FD29F',
//       },
//     ],
//   }}

function getGapsBetweenPerMonth(year: number) {
  return {
    1: 31,
    2: isLeapYear(year) ? 29 : 28,
    3: 31,
    4: 30,
    5: 31,
    6: 30,
    7: 31,
    8: 31,
    9: 30,
    10: 31,
    11: 30,
    12: 31,
  };
}

function getGradientColorSeries(dataset: any) {
  return dataset.map((s: any) => {
    const { data, name, color } = s;
    return {
      data: data.map((datum: number | string) => datum),
      name,
      type: 'line',
      itemStyle: {
        color: s.color,
      },
      smooth: true,
      areaStyle: {
        color: {
          type: 'linear',
          x: 0,
          y: 0,
          x2: 0,
          y2: 1,
          colorStops: [
            {
              offset: 0,
              color, // 颜色的起点
            },
            {
              offset: 1,
              color: 'rgba(255, 255, 255, 0)', // 颜色的终点
            },
          ],
          global: false, // 缺省为 false
        },
        opacity: 1,
      },
    };
  });
}

interface IChartData {
  time: string[];
  dataset: {
    name: string;
    data: number[];
    color: string;
  }[];
}

type TEChartGrid = {
  left?: string;
  right?: string;
  top?: string;
  bottom?: string;
};

type TYAxisScaleFactor = number;

interface IDashboardAreaChartProps {
  chartData: IChartData | undefined;
  range: TLongTermOptionValue;
  quantifier?: string;
  customGrid?: TEChartGrid | {};
  yAxisScaleFactor?: TYAxisScaleFactor;
  boundary?: number;
}

function getXAxisConfig(range: TLongTermOptionValue, chartData: IChartData) {
  const xAxisConfig: {
    type: string;
    data: string[];
    axisLabel: any;
    axisTick: any;
  } = {
    type: 'category',
    data: chartData.time,
    axisLabel: {
      formatter: () => {},
      rotate: 45,
    },
    axisTick: {
      alignWithLabel: true,
    },
  };

  switch (range) {
    case 'MONTH':
      xAxisConfig.axisLabel.formatter = (value: string) =>
        dayjs(value).format('DD');
      xAxisConfig.axisLabel.interval = 1;
      xAxisConfig.axisTick.interval = 1;
      break;
    case 'QUARTER':
      xAxisConfig.axisLabel.formatter = (value: string) =>
        dayjs(value).format('MM-DD');
      xAxisConfig.axisLabel.interval = 6;
      xAxisConfig.axisTick.interval = 6; // 每7天一個間隔
      break;
    case 'YEAR': {
      // 我们使用当前年份来计算每个月的天数
      const currentYear = dayjs().year();
      // 例如你想以当前月份对应的天数作为间隔，则可直接计算或者根据业务逻辑调整
      // 这里仅作一个示例：以当前年份的1月天数为 gap
      const gaps = getGapsBetweenPerMonth(currentYear);
      // 根据需求决定最终选取哪个月份的数字，例如这里选择1月的天数：
      let gapOutside = gaps[1];

      xAxisConfig.axisLabel.formatter = (value: string) => {
        const month = dayjs(value).format('MM');
        // Convert month string to number and ensure it's a valid index for gaps object
        const monthNum = parseInt(month, 10) as
          | 1
          | 2
          | 3
          | 4
          | 5
          | 6
          | 7
          | 8
          | 9
          | 10
          | 11
          | 12;
        gapOutside = gaps[monthNum];
        return month;
      };
      xAxisConfig.axisLabel.interval = gapOutside - 1;
      xAxisConfig.axisTick.interval = gapOutside - 1;
      break;
    }
    case 'ALL':
      xAxisConfig.axisLabel.formatter = (value: string) =>
        dayjs(value).format('MM');
      xAxisConfig.axisLabel.interval = 89;
      xAxisConfig.axisTick.interval = 89; // 每90天一個間隔
      break;
    default:
      xAxisConfig.axisLabel.formatter = (value: string) =>
        dayjs(value).format('YYYY-MM-DD');
      xAxisConfig.axisLabel.interval = 1;
      xAxisConfig.axisTick.interval = 1;
  }

  return xAxisConfig;
}

function DashboardAreaChart({
  chartData,
  range = 'MONTH',
  quantifier = '%',
  customGrid = {},
  yAxisScaleFactor = 1,
  boundary = 2,
}: IDashboardAreaChartProps) {
  const [eChartGrid, setEChartGrid] = useState<TEChartGrid>({
    left: '8%',
    right: '10%',
    top: '15%',
    bottom: '10%',
  });

  function adjustGridWidthByYAxisLabelLength() {
    if (!chartData || !chartData.dataset) return;
    const { dataset } = chartData;

    // 假設 quantifier 是一個字符串變量
    const thresholdLength = 4;

    // 檢查 dataset 中的數據
    const needsAdjustment = dataset.some((item: any) =>
      item.data.some(
        (datum: any) =>
          (formatNumberWithDigits(datum, 0) + quantifier + 1).length >=
          thresholdLength,
      ),
    );

    if (needsAdjustment) {
      let longestLabelLength = 0;
      dataset.forEach((item: any) => {
        item.data.forEach((datum: any) => {
          const formattedDatumLabel = `${formatNumberWithDigits(
            datum,
            0,
          )} ${quantifier}`;
          if (formattedDatumLabel.length > longestLabelLength) {
            longestLabelLength = formattedDatumLabel.length;
          }
        });
      });

      const leftGridWidth: number = longestLabelLength * 1.5;

      setEChartGrid((prevGrid) => ({
        ...prevGrid,
        left: `${String(leftGridWidth + 7)}%`,
      }));
    }
  }

  useEffect(() => {
    adjustGridWidthByYAxisLabelLength();
  }, [chartData, quantifier]);

  useEffect(() => {}, []);

  if (chartData === undefined)
    return (
      <div className="AreaChart__skeleton-wrapper">
        <Skeleton.Node active className="AreaChart__skeleton">
          <AreaChartOutlined style={{ fontSize: 156, color: '#bfbfbf' }} />
        </Skeleton.Node>
      </div>
    );

  function formatYAxisLabel(value: number) {
    // 對於小於 10 的數值保留一位小數，其他維持整數
    const digits = value < 10 ? 1 : 0;

    if (quantifier === '千元') {
      return `${formatNumberWithDigits(value / 1000, digits)} ${quantifier}`;
    }
    return `${formatNumberWithDigits(value, digits)} ${quantifier}`;
  }

  return (
    <ReactECharts
      option={{
        grid: {
          ...eChartGrid,
          ...customGrid,
        },
        tooltip: {
          position(
            pos: [number, number],
            params: any,
            dom: HTMLElement,
            rect: any,
            size: { contentSize: [number, number]; viewSize: [number, number] },
          ) {
            const [mouseX, mouseY] = pos; // 滑鼠的 x 和 y 座標
            const tooltipWidth = size.contentSize[0]; // tooltip 的寬度
            const containerWidth = size.viewSize[0]; // 容器的寬度

            let x = mouseX + 10; // 預設顯示在右側
            if (mouseX > containerWidth / boundary) {
              // 滑鼠在容器右側，將 tooltip 顯示在左側
              x = mouseX - tooltipWidth - 10;
            }

            // 固定 y 為滑鼠位置下方 10px
            const y = mouseY + 10;

            return [x, y];
          },

          trigger: 'axis',
          formatter(params: any) {
            // 取得 xAxis 名称
            const xAxisName = params[0].axisValueLabel;
            // 拼接自定义的 tooltip 内容
            let tooltipContent = `${xAxisName}<br>`;
            params.forEach((item: any) => {
              if (
                item.value === null ||
                item.value === undefined ||
                Number.isNaN(item.value)
              ) {
                tooltipContent += `${item.marker} ${item.seriesName} N/A<br>`;
              } else {
                tooltipContent += `${item.marker} ${item.seriesName} ${Number(
                  item.value,
                ).toFixed(1)} ${quantifier}<br>`;
              }
            });
            return tooltipContent;
          },
        },
        legend: {
          orient: 'horizontal',
          x: 'center',
          y: 'top',
          padding: [8, 0, 0],
          icon: 'circle',
          formatter(name: any) {
            if (chartData.dataset.length < 1) return;
            for (let i = 0; i < chartData.dataset.length; i += 1) {
              if (chartData.dataset[i].name === name) {
                // eslint-disable-next-line consistent-return
                return `${chartData.dataset[i].name}`;
              }
            }
          },
          selectedMode: false,
          itemStyle: {
            fill: (name: string) => {
              const series = chartData.dataset.find((s) => s.name === name);
              return series ? series.color : undefined;
            },
          },
          textStyle: {
            fontSize: 16, // Set the font size in pixels
          },
        },
        xAxis: getXAxisConfig(range, chartData),
        yAxis: {
          type: 'value',
          axisLabel: {
            width: 'auto',
            formatter: formatYAxisLabel,
          },
          ...(yAxisScaleFactor !== 1 && {
            max: (value: { max: number; min: number }) =>
              value.max * yAxisScaleFactor,
          }),
        },
        series: getGradientColorSeries(chartData.dataset),
      }}
      style={{ width: '400px', height: '290px', marginLeft: '2rem' }}
      opts={{ renderer: 'svg' }}
    />
  );
}

export default DashboardAreaChart;
